import {
  bridgeLoanSubTypes,
  DateFormat,
  formatDate,
} from '@roc/feature-utils';
import { formatCurrency, formatPercentage } from '@roc/ui/utils';
import { bridgeExperienceOptions, groundUpExperienceOptions, LoanSubType, MIXED_USE, MULTIFAMILY_5_PLUS, rentalExperienceOptions } from './constants';
import { verifiedInvestmentPropertiesFFOptions } from 'libs/feature-one-tool/src/quote/utils/constants';
import { FeeFormStore } from '../stores/common/feeFormStore';

export function getValueOrDefaultDash(value): string {
  return value ? value.toString() : '-';
}

export function getBooleanDisplayValue(value): string {
  return value ? 'Yes' : 'No';
}

export function getYesNoDisplayValue(value): string {
  return value === 'Y' ? 'Yes' : 'No';
}

export const sum = values =>
  values.reduce((total, value) => total + (Number(value) || 0), 0);

export const parseDate = date => {
  return date ? formatDate(date, DateFormat.MMDDYYYY) : '-';
};

export const LTC_CATCHUP_PERCENTAGE = 15;

export const calculateLtcCatchupAmount = purchasePrice => {
  return (LTC_CATCHUP_PERCENTAGE / 100) * purchasePrice;
};

export const isRestrictedCounty = (
  countyValue: string,
  notAllowedCounties: string[],
  state: string,
  notAllowedStates: string[]
): boolean => {
  if (!countyValue || !state) {
    return false;
  }

  const isRestrictedCount = notAllowedCounties.some(
    restricted =>
      countyValue === restricted ||
      countyValue?.startsWith(restricted + ' ') ||
      countyValue?.endsWith(' ' + restricted) ||
      countyValue?.includes(restricted)
  );

  return isRestrictedCount && notAllowedStates.includes(state);
};

export const buildAddress = property => {
  const address = property.address || '';
  const city = property.city || '';
  const state = property.state || '';
  const zipCode = property.zipCode || '';

  const fullAddress = `${address}, ${city}, ${state} ${zipCode}`.trim();

  return fullAddress;
};
export const formatPercentageValue = value => {
  if (value === null || value === undefined) {
    return '-';
  }
  return formatPercentage(value, 2);
};

export const isBridgeLoan = (loanSubtype: LoanSubType) => {
  return bridgeLoanSubTypes.includes(loanSubtype as any) || loanSubtype === LoanSubType.OTHER_BRIDGE;
};

export const getExperienceOptions = (loanSubtype: LoanSubType) => {
  switch (loanSubtype) {
    case LoanSubType.FIX_AND_FLIP:
    case LoanSubType.STABILIZED_BRIDGE:
    case LoanSubType.OTHER_BRIDGE:
      return bridgeExperienceOptions;
    case LoanSubType.SINGLE_PROPERTY:
    case LoanSubType.RENTAL_PORTFOLIO:
    case LoanSubType.OTHER_RENTAL:
      return rentalExperienceOptions;
    case LoanSubType.GROUND_UP:
      return groundUpExperienceOptions;
    default:
      return null;
  }
};

export const ynToBoolean = value => {
  return value === 'Y' ? true : value === 'N' ? false : null;
}

export const booleanToYn = value => {
  return value === true ? 'Y' : value === false ? 'N' : null;
}

export const pmt = (ir, np, pv, fv = 0, type = 0) => {
  /*
    * ir   - interest rate per month
    * np   - number of periods (months)
    * pv   - present value
    * fv   - future value
    * type - when the payments are due:
    *        0: end of the period, e.g. end of month (default)
    *        1: beginning of period
    */
  if (ir === 0) {
    return -(pv + fv) / np;
  }

  let pvif = Math.pow(1 + ir, np);
  let pmt = - ir * (pv * pvif + fv) / (pvif - 1);
  pmt = type === 1 ? pmt / (1 + ir) : pmt;
  return pmt;
}


export const calculateMonthlyPaymentAmortizing = (loanAmount, interestRate, months) => {
  const monthlyInterestRate = interestRate / 100 / 12;
  return pmt(monthlyInterestRate, months, -loanAmount, 0, 0);
}

export const calculateMonthlyPaymentNonAmortizing = (loanAmount, interestRate) => {
  const monthlyInterestRate = interestRate / 100 / 12;
  return loanAmount * monthlyInterestRate;
}

export const getUnitsByPropertyType = value => {
  switch (value) {
    case 'Single Family':
    case 'Townhome':
    case 'Planned Unit Development':
    case 'Condominium':
      return 1;
    case 'Duplex':
      return 2;
    case 'Triplex':
      return 3;
    case 'Quadruplex':
      return 4;
    case MULTIFAMILY_5_PLUS:
      return 5;
    case MIXED_USE:
      return 2;
    default:
      return 0;
  }
};

export const hasAtLeastOneNotFilledFee = (economics: any): boolean => {
  const feeValues = [
    economics.amounts?.interestReserve,
    economics.amounts?.thirdParyCosts,
    economics.amounts?.originationPoints,
    ...(economics.fees?.map(fee => fee.lenderPoints ?? fee.lenderAmount) ?? []),
  ];

  return feeValues.some(fee => !fee || fee == 0);
};